import { useCallback, useRef, useState } from 'react';
import { PagingQuery } from 'tea-component';

import {
  fetchRobloxGameRanks,
  parseRobloxGameRankResponse,
} from '@intelligence/v3/services/robloxGameRank';
import { handleTableDefaultSelectGames } from '@intelligence/utils/tableUtils';
import { ROBLOX_DEFAULT_SELECT_GAME_NUM, ROBLOX_RANK_DEFAULT_PAGE_SIZE, ROBLOX_GAME_RANK_SORT_KEY } from '../constants';
import { ROBLOX_GAME_RANK_DEFAULT_SOURCE } from '../config/robloxRankSourceConfig';
import { buildRobloxGameRankFetchParams } from '../utils/buildRobloxGameRankFetchParams';

import type { TableSelectionType, TableSelectStatusType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { IMainGenre } from '@intelligence/types/common';
import type { IRobloxResolvedDateRange } from '@intelligence/v3/utils/robloxFilterDate';
import type { IRobloxGameRankFilterValues, IRobloxGameRankRow } from '../types';

interface IFetchRobloxGameRankTableOptions {
  resetPage?: boolean;
  resetSelection?: boolean;
  keyword?: string;
  gameIds?: string[];
  rankGameIds?: string[];
  source?: string;
  rankOrder?: 'asc' | 'desc';
  rankSortKey?: string;
  fetchChart?: boolean;
  onAfterFetch?: (payload: {
    chartGameIds: string[];
    source: string;
    resolvedDateRange: IRobloxResolvedDateRange | null;
  }) => void | Promise<void>;
}

const getSelectionGameIds = (list: TableSelectionType[]): string[] => {
  return list
    .map((item) => String(item?.gameId || (item as IRobloxGameRankRow)?.game_id || ''))
    .filter(Boolean);
};

export default function useRobloxGameRankTable(
  robloxGenreOptionsRef?: React.MutableRefObject<IMainGenre[]>,
) {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<IRobloxGameRankRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(ROBLOX_RANK_DEFAULT_PAGE_SIZE);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);
  const [tableSelectionAll, setTableSelectionAll] = useState<TableSelectionType[]>([]);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [rankOrder, setRankOrder] = useState<'asc' | 'desc'>('desc');
  const [rankSortKey, setRankSortKey] = useState(ROBLOX_GAME_RANK_SORT_KEY);
  const gameIdsRef = useRef<string[]>([]);
  const rankGameIdsRef = useRef<string[]>([]);
  const sourceRef = useRef<string>(ROBLOX_GAME_RANK_DEFAULT_SOURCE);
  const rankOrderRef = useRef<'asc' | 'desc'>('desc');
  const rankSortKeyRef = useRef(ROBLOX_GAME_RANK_SORT_KEY);
  const pageIndexRef = useRef(1);
  const pageSizeRef = useRef(ROBLOX_RANK_DEFAULT_PAGE_SIZE);
  const tableSelectionRef = useRef<TableSelectionType[]>([]);

  const applyDefaultSelection = useCallback((list: IRobloxGameRankRow[]) => {
    const defaultGames = handleTableDefaultSelectGames(
      list.slice(0, ROBLOX_DEFAULT_SELECT_GAME_NUM),
      'game_id',
    );
    setTableSelectionAll(defaultGames);
    setTableSelection(defaultGames);
    tableSelectionRef.current = defaultGames;
    return defaultGames;
  }, []);

  const fetchTable = useCallback(async (
    filterValues: IRobloxGameRankFilterValues,
    options?: IFetchRobloxGameRankTableOptions,
  ) => {
    const nextGameIds = options?.gameIds !== undefined
      ? options.gameIds.filter(Boolean)
      : gameIdsRef.current;
    const nextRankGameIds = options?.rankGameIds !== undefined
      ? options.rankGameIds.filter(Boolean)
      : rankGameIdsRef.current;

    if (options?.rankGameIds !== undefined) {
      rankGameIdsRef.current = nextRankGameIds;
      gameIdsRef.current = [];
    } else if (options?.gameIds !== undefined) {
      gameIdsRef.current = nextGameIds;
      rankGameIdsRef.current = [];
    }

    const requestGameIds = rankGameIdsRef.current.length
      ? undefined
      : gameIdsRef.current;
    const requestRankGameIds = rankGameIdsRef.current.length
      ? rankGameIdsRef.current
      : undefined;
    const nextSource = options?.source ?? sourceRef.current;
    const nextPageIndex = options?.resetPage ? 1 : pageIndexRef.current;
    const nextPageSize = pageSizeRef.current;
    const shouldResetSelection = options?.resetSelection ?? options?.resetPage ?? false;
    const shouldResetRankOrder = shouldResetSelection;
    const nextRankOrder = shouldResetRankOrder
      ? 'desc'
      : (options?.rankOrder ?? rankOrderRef.current);
    const nextRankSortKey = shouldResetRankOrder
      ? ROBLOX_GAME_RANK_SORT_KEY
      : (options?.rankSortKey ?? rankSortKeyRef.current);

    sourceRef.current = nextSource;
    rankOrderRef.current = nextRankOrder;
    rankSortKeyRef.current = nextRankSortKey;
    setRankOrder(nextRankOrder);
    setRankSortKey(nextRankSortKey);
    setSelectedGameId(
      rankGameIdsRef.current[0]
      || gameIdsRef.current[0]
      || '',
    );
    setLoading(true);

    if (shouldResetSelection || options?.fetchChart) {
      setRecords([]);
      setTotal(0);
    }

    try {
      const params = buildRobloxGameRankFetchParams(filterValues, {
        page: nextPageIndex,
        pageSize: nextPageSize,
        gameIds: requestGameIds,
        rankGameIds: requestRankGameIds,
        source: nextSource,
        rankOrder: nextRankOrder,
        rankSortKey: nextRankSortKey,
        robloxGenreOptions: robloxGenreOptionsRef?.current,
      });
      const response = await fetchRobloxGameRanks(params);
      const { list, total: nextTotal, resolvedDateRange } = parseRobloxGameRankResponse(response);

      setRecords(list);
      setTotal(nextTotal);
      setPageIndex(nextPageIndex);
      pageIndexRef.current = nextPageIndex;

      let chartGameIds: string[] = [];

      if (nextPageIndex === 1 && shouldResetSelection) {
        const defaultGames = applyDefaultSelection(list);
        chartGameIds = getSelectionGameIds(defaultGames);
      } else {
        chartGameIds = getSelectionGameIds(tableSelectionRef.current);
      }

      if (options?.fetchChart && options?.onAfterFetch) {
        await Promise.resolve(options.onAfterFetch({
          chartGameIds,
          source: nextSource,
          resolvedDateRange,
        }));
      }
    } catch (error) {
      console.debug('[RobloxGameRank] fetch table failed', error);
      setRecords([]);
      setTotal(0);
      if (shouldResetSelection) {
        setTableSelection([]);
        setTableSelectionAll([]);
        tableSelectionRef.current = [];
      }

      if (options?.fetchChart && options?.onAfterFetch) {
        await Promise.resolve(options.onAfterFetch({
          chartGameIds: [],
          source: nextSource,
          resolvedDateRange: null,
        }));
      }
    } finally {
      setLoading(false);
    }
  }, [applyDefaultSelection]);

  const handlePagingChange = useCallback((filterValues: IRobloxGameRankFilterValues, paging: PagingQuery) => {
    const nextPageIndex = paging.pageIndex || 1;
    const nextPageSize = paging.pageSize || ROBLOX_RANK_DEFAULT_PAGE_SIZE;

    pageIndexRef.current = nextPageIndex;
    pageSizeRef.current = nextPageSize;
    setPageSize(nextPageSize);

    fetchTable(filterValues, {
      resetPage: false,
      resetSelection: false,
    });
  }, [fetchTable]);

  const handleCheckGame = useCallback((
    list: TableSelectionType[],
    _status: TableSelectStatusType,
  ) => {
    setTableSelection(list);
    tableSelectionRef.current = list;
  }, []);

  const handleSelectGame = useCallback((filterValues: IRobloxGameRankFilterValues, gameId: string) => {
    if (!gameId) {
      return;
    }

    fetchTable(filterValues, {
      rankGameIds: [gameId],
      resetPage: true,
      resetSelection: true,
    });
  }, [fetchTable]);

  const handleClearSearch = useCallback((filterValues: IRobloxGameRankFilterValues) => {
    fetchTable(filterValues, {
      gameIds: [],
      resetPage: true,
      resetSelection: true,
    });
  }, [fetchTable]);

  return {
    loading,
    records,
    total,
    pageIndex,
    pageSize,
    selectedGameId,
    rankOrder,
    rankSortKey,
    tableSelection,
    tableSelectionAll,
    fetchTable,
    handlePagingChange,
    handleCheckGame,
    handleSelectGame,
    handleClearSearch,
  };
}
